import React, { useState } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { TextArea } from '@progress/kendo-react-inputs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  FinanceQuoteFieldDiff,
  DeferredPaymentCollectionDiff,
  IrregularPaymentStreamCollectionDiff,
} from '../../Data/FinanceQuoteComparisonData';

export interface QuoteChangeDiffDialogProps {
  differences: FinanceQuoteFieldDiff[];
  deferredPaymentDiffs: DeferredPaymentCollectionDiff | null;
  irregularPaymentStreamDiffs: IrregularPaymentStreamCollectionDiff | null;
  printedAt: string;
  closeDialog: () => void;
  accept: (comment: string) => void;
}

const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString();
const formatCurrency = (amount: number) => amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/**
 * Dialog that shows a side-by-side comparison of finance quote fields that changed
 * between when documents were printed and the current quote state.
 * Includes deferred down payments and irregular payment streams.
 * Requires a mandatory comment before the user can acknowledge and continue with the loan post.
 */
const QuoteChangeDiffDialog: React.FC<QuoteChangeDiffDialogProps> = ({
  differences,
  deferredPaymentDiffs,
  irregularPaymentStreamDiffs,
  printedAt,
  closeDialog,
  accept
}) => {
  const [comment, setComment] = useState('');

  const printedDate = new Date(printedAt).toLocaleString();
  const isCommentValid = comment.trim().length > 0;

  return (
    <>
      <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <WarningAmberIcon color="warning" style={{ marginRight: '8px' }} />
          Quote Changed Since Documents Were Printed
        </div>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          The finance quote has changed since documents were last printed on <strong>{printedDate}</strong>.
          Please review the differences below and provide a comment explaining why you are proceeding.
        </Typography>
        
        {/* Field-level differences */}
        {differences.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Field</strong></TableCell>
                  <TableCell><strong>At Print Time</strong></TableCell>
                  <TableCell><strong>Current Value</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {differences.map((diff) => (
                  <TableRow key={diff.fieldName}>
                    <TableCell>{diff.displayName}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>{diff.printedValue}</TableCell>
                    <TableCell sx={{ color: 'success.main' }}>{diff.currentValue}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Deferred down payments */}
        {deferredPaymentDiffs?.hasDifferences && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Deferred Down Payments (Printed: {deferredPaymentDiffs.printedCount}, Current: {deferredPaymentDiffs.currentCount})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ color: 'error.main' }}><strong>At Print Time</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deferredPaymentDiffs.printedPayments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>None</TableCell>
                      </TableRow>
                    ) : (
                      deferredPaymentDiffs.printedPayments.map((dp, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{formatDate(dp.paymentDate)}</TableCell>
                          <TableCell>{formatCurrency(dp.amount)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ color: 'success.main' }}><strong>Current</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deferredPaymentDiffs.currentPayments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>None</TableCell>
                      </TableRow>
                    ) : (
                      deferredPaymentDiffs.currentPayments.map((dp, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{formatDate(dp.paymentDate)}</TableCell>
                          <TableCell>{formatCurrency(dp.amount)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}

        {/* Irregular payment streams */}
        {irregularPaymentStreamDiffs?.hasDifferences && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Irregular Payments (Printed: {irregularPaymentStreamDiffs.printedCount}, Current: {irregularPaymentStreamDiffs.currentCount})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, maxHeight: 250, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ color: 'error.main' }}><strong>At Print Time</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Start</strong></TableCell>
                      <TableCell><strong>End</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                      <TableCell><strong>Count</strong></TableCell>
                      <TableCell><strong>Frequency</strong></TableCell>
                      <TableCell><strong>Handling</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {irregularPaymentStreamDiffs.printedStreams.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>None</TableCell>
                      </TableRow>
                    ) : (
                      irregularPaymentStreamDiffs.printedStreams.map((s, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{s.type}</TableCell>
                          <TableCell>{formatDate(s.startDate)}</TableCell>
                          <TableCell>{formatDate(s.endDate)}</TableCell>
                          <TableCell>{formatCurrency(s.amount)}</TableCell>
                          <TableCell>{s.count}</TableCell>
                          <TableCell>{s.paymentFrequency}</TableCell>
                          <TableCell>{s.handling}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, maxHeight: 250, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ color: 'success.main' }}><strong>Current</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Start</strong></TableCell>
                      <TableCell><strong>End</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                      <TableCell><strong>Count</strong></TableCell>
                      <TableCell><strong>Frequency</strong></TableCell>
                      <TableCell><strong>Handling</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {irregularPaymentStreamDiffs.currentStreams.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>None</TableCell>
                      </TableRow>
                    ) : (
                      irregularPaymentStreamDiffs.currentStreams.map((s, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{s.type}</TableCell>
                          <TableCell>{formatDate(s.startDate)}</TableCell>
                          <TableCell>{formatDate(s.endDate)}</TableCell>
                          <TableCell>{formatCurrency(s.amount)}</TableCell>
                          <TableCell>{s.count}</TableCell>
                          <TableCell>{s.paymentFrequency}</TableCell>
                          <TableCell>{s.handling}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Comment (required):</strong>
          </Typography>
          <TextArea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.value ?? '')}
            placeholder="Explain why you are posting with a different quote than what was printed..."
            style={{ width: '100%' }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => accept(comment.trim())}
          color="warning"
          variant="contained"
          disabled={!isCommentValid}
        >
          Acknowledge &amp; Continue
        </Button>
      </DialogActions>
    </>
  );
};

export default QuoteChangeDiffDialog;
